/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Page Auth Provider & Context
 *
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.11.0
 */

import { createContext, useContext, FC } from 'react';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import initTranslationPageAuth from '../../modules/internationalization/constants/pages/auth';
import {
    makeSelectTranslationPageAuth,
    makeSelectTranslationPageAuthServiceAccess,
} from '../../modules/internationalization/intSelectors';
import { IPageAuthServiceAccessTranslation, IPageAuthTranslation } from '../../modules/internationalization/types';
import { loginProcessAction } from './actions/authActions';
import { loginProcessServiceAccessAction } from './actions/authServiceAccessActions';
import { ILoginProcessRequest } from './types/authTypes';

interface IPageAuthContext {
    isServiceUser: boolean;
    translation: IPageAuthTranslation | IPageAuthServiceAccessTranslation;

    loginProcess: (form: ILoginProcessRequest) => void;
}

const defaultValue: IPageAuthContext = {
    isServiceUser: false,
    translation: initTranslationPageAuth,

    loginProcess: () => null,
};

const PageAuthContext = createContext<IPageAuthContext>(defaultValue);

export const useAuthContext = () => {
    const context = useContext(PageAuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within a PageAuthProvider')
    }
    return context
}

const PageAuthProvider: FC<{ isServiceUser: boolean }> = props => {
    const { children, isServiceUser } = props;

    // get translation
    const translationPageAuth = useSelector(makeSelectTranslationPageAuth(), shallowEqual);
    const translationPageAuthServiceAccess = useSelector(makeSelectTranslationPageAuthServiceAccess(), shallowEqual);
    const translation = isServiceUser ? translationPageAuthServiceAccess : translationPageAuth;

    // get login process action
    const dispatch = useDispatch();
    const loginProcess: (form: ILoginProcessRequest) => void = (form: ILoginProcessRequest) => isServiceUser
        ? dispatch(loginProcessServiceAccessAction(form))
        : dispatch(loginProcessAction(form))

    return (
        <PageAuthContext.Provider value={{ isServiceUser, translation, loginProcess }}>
            {children}
        </PageAuthContext.Provider>
    );
};

export default PageAuthProvider;
